import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { BaseComponentProps } from "../types";
import { alpha } from "../../typography";
import { IconName, SmallPlainIcon } from "../Icon";
import React, { useEffect, useRef, useState } from "react";
import { LabelText, Asterisk, Caption } from "../common";

type StyledTextFieldContainerProps = {
  disabled?: boolean;
  readOnly?: boolean;
  hasError?: boolean;
  styles?: FlattenSimpleInterpolation;
};

const StyledTextFieldContainer = styled.div<StyledTextFieldContainerProps>`
  position: relative;
  display: flex;
  box-sizing: border-box;
  height: 32px;
  background: var(--color-neutral-000-full);
  border: 1px solid var(--color-primary-400);
  border-radius: var(--size-border-radius-small);
  :focus-within {
    outline: none;
  }
  ${(props) =>
    props.hasError
      ? css`
          border: 1px solid var(--color-error-500);
        `
      : props.disabled || props.readOnly
      ? css`
          border: 1px solid var(--color-primary-200);
          input::placeholder {
            color: var(--color-primary-200);
          }
        `
      : css`
          &:hover {
            border: 1px solid var(--color-primary-800);
          }
          &:focus-within {
            border: 1px solid var(--color-secondary-800);
          }
        `}
  ${(props) => `${props.styles}`}
`;

const StyledTextField = styled.input`
  display: block;
  min-width: 0px;
  flex-grow: 1;
  padding: 0px 12px;
  padding-right: 34px;
  background: inherit;
  border: none;
  border-radius: var(--size-border-radius-small);
  ${(props) =>
    props.disabled ? alpha.primaryDisabled.body3 : alpha.primaryInactive.body3};

  :focus {
    outline: none;
    ${alpha.primaryActive.body3}
  }

  ::placeholder {
    ${alpha.primaryInactive.body3};
  }

  ::selection {
    ${alpha.primaryActive.body3}
    background-color: var(--color-secondary-200);
  }
`;

const StyledIconContainer = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  right: 12px;
  bottom: 0;
  ${(props) =>
    props.hasError &&
    css`
      svg {
        --main-color: var(--color-error-500);
      }
    `}
`;

export type TextFieldProps = BaseComponentProps<"input"> & {
  caption?: string;
  errorMessage?: string;
  iconName?: IconName;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  styles?: FlattenSimpleInterpolation;

  /** Function called when the icon is clicked */
  iconOnClick?: (event: React.MouseEvent<HTMLElement>) => void;

  /** Function called when the input looses focus */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /** Function called when the input value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const TextFieldComponent = ({
  caption,
  errorMessage,
  iconName,
  iconOnClick,
  onBlur,
  onChange,
  ...inputProps
}: TextFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focused, inputRef]);

  return (
    <>
      <StyledTextFieldContainer
        disabled={inputProps.disabled}
        readOnly={inputProps.readOnly}
        hasError={!!errorMessage}
        styles={inputProps.styles}
      >
        <StyledTextField
          ref={inputRef}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            onBlur && onBlur(event);
            setFocused(false);
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onChange && onChange(event);
          }}
          onFocus={() => setFocused(true)}
          {...inputProps}
        />

        {iconName && (
          <StyledIconContainer hasError={!!errorMessage} onClick={iconOnClick}>
            <SmallPlainIcon
              iconName={errorMessage ? "exclamation" : iconName}
              variant={
                inputProps.disabled || inputProps.readOnly
                  ? "DISABLED"
                  : focused
                  ? "ACTIVE"
                  : "DEFAULT"
              }
            />
          </StyledIconContainer>
        )}
      </StyledTextFieldContainer>
      {(caption || errorMessage) && (
        <Caption
          isDisabled={inputProps.disabled || inputProps.readOnly}
          hasError={!!errorMessage}
        >
          {errorMessage || caption}
        </Caption>
      )}
    </>
  );
};

export const TextField = (props: TextFieldProps) => {
  const { label, name, disabled, errorMessage, isRequired } = props;

  if (label) {
    return (
      <label htmlFor={name}>
        <LabelText isDisabled={disabled} hasError={!!errorMessage}>
          {label} {isRequired ? <Asterisk>*</Asterisk> : null}
        </LabelText>
        <TextFieldComponent aria-label={label} {...props} />
      </label>
    );
  }
  return <TextFieldComponent {...props} />;
};
